import pagesIndex from "app/components/React/QuickSearch/pageData/pages_index.json";

export type QuickSearchCatalogLocale = "en" | "es" | "fr" | "ko" | "zh" | "ja";

export type QuickSearchCatalogLocalizedString = {
    en: string;
    es?: string;
    fr?: string;
    ko?: string;
    zh?: string;
    ja?: string;
};

export type QuickSearchCatalogPage = {
    id: string;
    title: string;
    url: string;
    breadcrumbs: string[];
    summary: string;
    uiRouterState?: string;
    isExternal?: boolean;
    isLocked?: boolean;
    hookKey?: string;
};

type PagesIndexNode = {
    type?: string;
    title?: string | QuickSearchCatalogLocalizedString;
    url?: string;
    id?: string;
    summary?: string | QuickSearchCatalogLocalizedString;
    items?: PagesIndexNode[];
    uiRouterState?: string;
    isExternal?: boolean;
};

const pickCatalogLocale = (i18nLocale: string): QuickSearchCatalogLocale => {
    const base = i18nLocale.split("-")[0]?.toLowerCase() ?? "en";
    if (base === "es") {
        return "es";
    }
    if (base === "fr") {
        return "fr";
    }
    if (base === "ko") {
        return "ko";
    }
    if (base === "ja") {
        return "ja";
    }
    if (base === "zh") {
        return "zh";
    }
    return "en";
};

export const resolveQuickSearchCatalogLocalizedString = (
    value: string | QuickSearchCatalogLocalizedString | undefined,
    locale: QuickSearchCatalogLocale,
): string => {
    if (value == null) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    const localized = value[locale];
    if (typeof localized === "string" && localized.length > 0) {
        return localized;
    }
    if (typeof value.en === "string" && value.en.length > 0) {
        return value.en;
    }
    return "";
};

const collectPagesFromItems = (
    items: PagesIndexNode[] | undefined,
    ancestorTitles: string[],
    out: QuickSearchCatalogPage[],
    locale: QuickSearchCatalogLocale,
): void => {
    if (!items) {
        return;
    }

    for (const node of items) {
        if (node.type === "page" && node.title && node.url) {
            out.push({
                id: node.id ?? node.url,
                title: resolveQuickSearchCatalogLocalizedString(node.title, locale),
                url: node.url,
                breadcrumbs: ancestorTitles.filter(Boolean),
                summary: resolveQuickSearchCatalogLocalizedString(node.summary, locale),
                uiRouterState: typeof node.uiRouterState === "string" ? node.uiRouterState : undefined,
                isExternal: node.isExternal === true ? true : undefined,
            });
        }

        const resolvedTitle =
            node.type && node.type !== "page" && node.title
                ? resolveQuickSearchCatalogLocalizedString(node.title, locale)
                : "";
        const nextAncestors =
            node.type && node.type !== "page" && resolvedTitle ? [...ancestorTitles, resolvedTitle] : ancestorTitles;

        if (node.items?.length) {
            collectPagesFromItems(node.items, nextAncestors, out, locale);
        }
    }
};

/** Depth-first catalog order: navigation_groups → nested items; only `type: "page"` nodes. */
export const getQuickSearchCatalogPages = (i18nLocale = "en"): QuickSearchCatalogPage[] => {
    const root = pagesIndex as Record<string, unknown>;
    const groups = root["navigation_groups"] as
        | {
              title?: string | QuickSearchCatalogLocalizedString;
              items?: PagesIndexNode[];
          }[]
        | undefined;
    const out: QuickSearchCatalogPage[] = [];
    const locale = pickCatalogLocale(i18nLocale);

    for (const group of groups ?? []) {
        const groupTitle = group.title ? resolveQuickSearchCatalogLocalizedString(group.title, locale) : "";
        const startPath = groupTitle ? [groupTitle] : [];
        collectPagesFromItems(group.items, startPath, out, locale);
    }

    return out;
};
